import React, {useMemo} from 'react';
import {useParams} from 'react-router-dom';
import moment from 'moment';
import {useQuery} from '@apollo/client';
import {taskFields, taskValues} from './tasksFields';
import {URGENCY} from '../../../constants/Urgency';
import {useAccountContext} from '../../../contexts/AccountContext';
import Loading from '../../../components/Loading/Loading';
import useTitle from '../../../hooks/useTitle';
import {
  ListTasks,
  ListTasksVariables,
  ListTasks_tasks,
} from '../../../schema-types/ListTasks';
import {LIST_TASKS} from '../tasksQueries';
import {taskValidation} from './taskValidation';
import OWNERSHIP from '../../../constants/Ownership';
import {
  OneviewTaskSelectedEntityTypeChoices,
  TaskRegardingEntityEnum,
} from '../../../schema-types/globalTypes';
import {TasksForm} from './TasksForm';
import validateUUID from '../../../utils/validateUUID';

const CreateTask = () => {
  const {reviewId, taskId} = useParams();
  const {account} = useAccountContext();
  useTitle(`Create task - ${account?.name}`);
  const {data, error, loading} = useQuery<ListTasks, ListTasksVariables>(LIST_TASKS, {
    variables: {
      reviewId: validateUUID(reviewId),
    },
    fetchPolicy: 'network-only',
  });

  const initialValues = useMemo<taskValues | undefined>(() => {
    if (!taskId || !data) {
      return undefined;
    }
    const task = data?.tasks?.find((x) => x.id === taskId);
    if (!task) {
      return undefined;
    }
    const selectEntityType = (key: keyof ListTasks_tasks): TaskRegardingEntityEnum => {
      if ((task as any)[key] === OneviewTaskSelectedEntityTypeChoices.CUROGENERICMODEL) {
        return TaskRegardingEntityEnum.THIRD_PARTY;
      }

      if ((task as any)[key] === OneviewTaskSelectedEntityTypeChoices.POT) {
        return TaskRegardingEntityEnum.PLAN;
      }

      return (task as any)[key] as TaskRegardingEntityEnum;
    };

    const init: taskValues = {
      taskId: task.id,
      deadline: moment.utc(task.dueDate),
      contact: task.contact?.id || OWNERSHIP.JOINT_CODE,
      urgency: task.urgency ? (URGENCY as any)[task.urgency] : undefined,
      notes: task.description || '',
      subgroupId: task.activitySubGroup?.id,
      taskEntityOption:
        selectEntityType('regardingType') || TaskRegardingEntityEnum.CONTACT,
      regarding: task.selectedEntityId ? String(task.selectedEntityId) : undefined,
      selectedEntityType:
        selectEntityType('selectedEntityType') || TaskRegardingEntityEnum.CONTACT,
      // a convenient way to load regardingThirdParty name
      selectedEntityTypeDetails: task.regardingThirdParty?.name || '',
      openAccordionGroup: task.activityGroup?.formattedName || '',
    };
    return init;
  }, [taskId, data]);

  useTitle(`New task - ${account?.name}`);
  if (taskId && !data) {
    return <Loading />;
  }
  return (
    <TasksForm
      validate={taskValidation}
      initialValueOverrides={initialValues}
      formFields={taskFields}
    />
  );
};

export default CreateTask;
